/**
 * "What would this choice cost me?" — the number beside every swatch.
 *
 * Computed from the SAME rows and the SAME estimator the pricing summary
 * uses, so the delta a swatch promises is the delta the total actually moves
 * by. Anything cheaper (a hard-coded kr/m² per species, say) would be a second
 * source of truth that drifts the first time a price rule changes.
 */
import type { MaterialRow } from "../../engines/types";
import { estimateMaterialRowPrice } from "../pricing/priceEstimator";
import {
	calculateExteriorTreatment,
	EXTERIOR_TREATMENT_MATERIAL_KEY,
} from "./calculateExteriorTreatment";
import {
	INTERIOR_WOOD_BENCH_KEYS,
	INTERIOR_WOOD_CATALOG,
	INTERIOR_WOOD_PANEL_KEYS,
} from "./finishCatalog";
import {
	DEFAULT_FINISHES,
	EXTERIOR_TREATMENTS,
	type ExteriorTreatmentId,
	type FinishConfig,
	INTERIOR_WOOD_SPECIES,
	type InteriorPanelDirection,
	type InteriorWoodSpecies,
	PANEL_DIRECTIONS,
} from "./types";

export interface FinishPriceDeltas {
	interiorWood: Record<InteriorWoodSpecies, number>;
	panelDirection: Record<InteriorPanelDirection, number>;
	exteriorTreatment: Record<ExteriorTreatmentId, number>;
}

/**
 * The engine-owned inputs a delta needs but a BOM row cannot supply:
 * the facade area behind the litre count, and the batten lpm the *other*
 * panel direction would buy (standing battens are one run per stud line,
 * lying battens are rows per wall — they are not the same number).
 */
export interface FinishPricingBasis {
	facadeAreaSqm: number;
	/** Interior glespanel lpm under the direction that is NOT selected. */
	alternateBattenLpm?: number;
	/** Interior glespanel lpm under the SELECTED direction. */
	selectedBattenLpm?: number;
}

/** The BOM row the panel-direction delta reprices. */
const BATTEN_MATERIAL_KEY = "interior.batten";

const PANEL_KEYS = new Set(INTERIOR_WOOD_PANEL_KEYS);
const BENCH_KEYS = new Set(INTERIOR_WOOD_BENCH_KEYS);

function priceOf(row: MaterialRow): number {
	return estimateMaterialRowPrice(row, { mode: "baseline" }).price;
}

function repriced(row: MaterialRow, priceRuleId: string): number {
	return priceOf({ ...row, priceRuleId });
}

/**
 * Delta in SEK for every option of every choice, relative to `finishes`.
 * The selected option is always exactly 0.
 */
export function calculateFinishPriceDeltas(input: {
	/** The current calculation's material rows, finishes already applied. */
	materials: MaterialRow[];
	finishes?: FinishConfig;
	basis?: FinishPricingBasis;
}): FinishPriceDeltas {
	const finishes = input.finishes ?? DEFAULT_FINISHES;
	const basis = input.basis;

	// ── Interior wood ──────────────────────────────────────────
	const woodRows = input.materials.filter(
		(row) =>
			row.materialKey &&
			(PANEL_KEYS.has(row.materialKey) || (BENCH_KEYS.has(row.materialKey) && !row.woodSpecies)),
	);
	const woodBaseline = woodRows.reduce((sum, row) => sum + priceOf(row), 0);

	const interiorWood = Object.fromEntries(
		INTERIOR_WOOD_SPECIES.map((species) => {
			// The rows arrive already stamped with the SELECTED species' rules,
			// so repricing them under that same species just recomputes the
			// baseline. Its delta is 0 by construction.
			if (species === finishes.interiorWood) return [species, 0];
			const spec = INTERIOR_WOOD_CATALOG[species];
			const total = woodRows.reduce(
				(sum, row) =>
					sum +
					repriced(
						row,
						PANEL_KEYS.has(row.materialKey as string)
							? spec.panelPriceRuleId
							: spec.benchPriceRuleId,
					),
				0,
			);
			return [species, total - woodBaseline];
		}),
	) as Record<InteriorWoodSpecies, number>;

	// ── Exterior treatment ─────────────────────────────────────
	const treatmentBaseline = input.materials
		.filter((row) => row.materialKey === EXTERIOR_TREATMENT_MATERIAL_KEY)
		.reduce((sum, row) => sum + priceOf(row), 0);

	const exteriorTreatment = Object.fromEntries(
		EXTERIOR_TREATMENTS.map((treatment) => {
			if (treatment === finishes.exteriorTreatment) return [treatment, 0];
			const result = calculateExteriorTreatment({
				facadeAreaSqm: basis?.facadeAreaSqm ?? 0,
				finishes: { ...finishes, exteriorTreatment: treatment },
			});
			const total = result.materials.reduce(
				(sum, row) => sum + priceOf(row),
				0,
			);
			return [treatment, total - treatmentBaseline];
		}),
	) as Record<ExteriorTreatmentId, number>;

	// ── Panel direction ────────────────────────────────────────
	// The panel lpm is direction-independent (area ÷ cover width), so the only
	// money that moves is the korsläkt underneath it: standing panel needs
	// lying battens, lying panel needs standing ones, and those are different
	// quantities. Reprice the REAL batten row at the other direction's lpm
	// rather than synthesising one — a hand-built row would carry a third copy
	// of the batten's price rule and drift from the two beside it.
	const battenRow = input.materials.find(
		(row) => row.materialKey === BATTEN_MATERIAL_KEY,
	);
	const canPriceDirection =
		battenRow !== undefined &&
		basis?.alternateBattenLpm !== undefined &&
		basis.selectedBattenLpm !== undefined;
	const directionDelta = canPriceDirection
		? priceOf({
				...battenRow,
				quantity: `${Math.ceil(basis.alternateBattenLpm as number)} lpm`,
			}) -
			priceOf({
				...battenRow,
				quantity: `${Math.ceil(basis.selectedBattenLpm as number)} lpm`,
			})
		: 0;

	const panelDirection = Object.fromEntries(
		PANEL_DIRECTIONS.map((direction) => [
			direction,
			direction === finishes.panelDirection ? 0 : directionDelta,
		]),
	) as Record<InteriorPanelDirection, number>;

	return { interiorWood, panelDirection, exteriorTreatment };
}
